import React, { useCallback, useEffect, useRef, useState } from 'react';
import Avatar from '@atlaskit/avatar';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import ColorPicker from '@atlaskit/color-picker';
import Form, { ErrorMessage, Field, FormSection } from '@atlaskit/form';
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { useIntl } from '@atlassian/jira-intl';
import {
	PRIORITY_NAME_FIELD,
	PRIORITY_NAME_MAX_LENGTH,
	PRIORITY_DESC_FIELD,
	PRIORITY_DESCRIPTION_MAX_LENGTH,
	PRIORITY_ICON_FIELD,
	PRIORITY_STATUS_COLOR_FIELD,
} from '../../../../common/constants';
import type {
	CreateAndEditPriorityRequestBody,
	Priority,
	PriorityAvatar,
	PriorityFormAsyncErrors,
} from '../../../../common/types';
import { ModalAvatarPicker } from './avatar-picker-modal';
import { colorPalette } from './constants';
import { messages } from './messages';

export type PriorityFormFieldsProps = {
	initialPriority?: Priority;
	asyncFormErrors?: PriorityFormAsyncErrors;
	onHandleSubmit: (data: CreateAndEditPriorityRequestBody, priority?: Priority) => void;
};

export type PriorityFormFooterProps = {
	confirmText: string;
	onCancel: () => void;
	submitting?: boolean;
};

export const PriorityFooter = ({ confirmText, onCancel, submitting }: PriorityFormFooterProps) => {
	const { formatMessage } = useIntl();
	return (
		<ButtonGroup>
			<Button
				appearance="subtle"
				onClick={onCancel}
				type="button"
				label={formatMessage(messages.cancelButton)}
			>
				{formatMessage(messages.cancelButton)}
			</Button>
			<LoadingButton
				title={confirmText}
				appearance="primary"
				isLoading={submitting}
				form="priority-form"
				type="submit"
			>
				{confirmText}
			</LoadingButton>
		</ButtonGroup>
	);
};

export const PriorityForm = ({
	initialPriority,
	asyncFormErrors,
	onHandleSubmit,
}: PriorityFormFieldsProps) => {
	const { formatMessage } = useIntl();

	const [shouldDisplayAvatarPicker, setShouldDisplayAvatarPicker] = useState(false);
	const [selectedAvatarDataUri, setSelectedAvatarDataUri] = useState<string | undefined>(
		initialPriority?.iconUrl ?? undefined,
	);

	const onOpenAvatarPicker = () => setShouldDisplayAvatarPicker(true);
	const onCloseAvatarPicker = () => {
		setShouldDisplayAvatarPicker(false);
	};

	const nameInputRef = useRef<HTMLInputElement | null>(null);

	const palette = colorPalette.map(({ value, label }) => ({
		label: formatMessage(label),
		value,
	}));

	const onValidateName = useCallback(
		(value: string | undefined) => {
			if (!value) {
				return formatMessage(messages.nameValidationErrorRequired);
			}

			if (value === asyncFormErrors?.duplicateName) {
				return formatMessage(messages.nameValidationErrorDuplicateName, {
					name: asyncFormErrors.duplicateName,
				});
			}
		},
		[asyncFormErrors, formatMessage],
	);

	/**
	 * If icon field is empty:
	 * 	- check if there as an iconUrl from the initialPriority field, if
	 * there is, this means that the priority icon field is defined by a hardcoded
	 * iconUrl, not by the universal avatar apis (old method of storing avatars).
	 * TBD how we are going to handle this case in https://donut-world-dogfood.atlassian.net/browse/EAX-213
	 * For now it will be seamless intergration.
	 * - if not then there is nothing set and we throw an error
	 * @param value
	 * @returns
	 */
	const onValidateIconField = (value: string | undefined) => {
		if (value || (initialPriority && initialPriority.iconUrl !== '')) return;

		return formatMessage(messages.iconValidationError);
	};

	const onValidateStatusColor = (value: string | undefined) => {
		if (!value) {
			return formatMessage(messages.colorPickerValidationError);
		}
	};

	useEffect(() => {
		if (asyncFormErrors?.duplicateName) {
			onValidateName(asyncFormErrors.duplicateName);
			nameInputRef?.current?.focus();
		}
	}, [asyncFormErrors, onValidateName]);

	// Wrapper function is needed for the onSubmit function so when it's mock function is called in the unit tests,
	// it's called with the correct arguments.
	const onSubmit = (data: CreateAndEditPriorityRequestBody) => {
		// The primary CTA button in ModalAvatarPicker has a [type="submit"] attribute
		// The following condition prevents the form from being submitted when a user selects an icon:
		if (shouldDisplayAvatarPicker) return undefined;

		onHandleSubmit(data);
	};

	return (
		<Form onSubmit={onSubmit}>
			{({ formProps, setFieldValue }) => {
				const setSelectedAvatar = (selectedAvatar: PriorityAvatar | undefined) => {
					if (selectedAvatar) {
						setFieldValue(PRIORITY_ICON_FIELD, selectedAvatar.id ?? initialPriority?.avatarId);
						setSelectedAvatarDataUri(selectedAvatar.dataURI);
					}
				};

				return (
					<form id="priority-form" {...formProps}>
						<FormSection>
							<Field
								name={PRIORITY_NAME_FIELD}
								label={formatMessage(messages.nameFieldLabel)}
								defaultValue={initialPriority?.name}
								isRequired
								validate={onValidateName}
							>
								{({ fieldProps, error }) => (
									<Stack>
										<TextField
											{...fieldProps}
											ref={nameInputRef}
											name={PRIORITY_NAME_FIELD}
											onChange={(value) => fieldProps.onChange(value)}
											maxLength={PRIORITY_NAME_MAX_LENGTH}
											placeholder={formatMessage(messages.nameFieldPlaceholder)}
										/>
										{error && <ErrorMessage>{error}</ErrorMessage>}

										{/* A duplicate name error message to display before the form is aware of the error */}
										{!error && nameInputRef.current?.value === asyncFormErrors?.duplicateName && (
											<ErrorMessage>
												{formatMessage(messages.nameValidationErrorDuplicateName, {
													name: asyncFormErrors?.duplicateName,
												})}
											</ErrorMessage>
										)}
									</Stack>
								)}
							</Field>
							<Field
								name={PRIORITY_DESC_FIELD}
								label={formatMessage(messages.descriptionFieldLabel)}
								defaultValue={initialPriority?.description}
							>
								{({ fieldProps, error }) => (
									<Stack>
										<TextField
											{...fieldProps}
											name={PRIORITY_DESC_FIELD}
											onChange={(value) => fieldProps.onChange(value)}
											maxLength={PRIORITY_DESCRIPTION_MAX_LENGTH}
											placeholder={formatMessage(messages.descriptionFieldPlaceholder)}
										/>
										{error && <ErrorMessage>{error}</ErrorMessage>}
									</Stack>
								)}
							</Field>
							<Field
								name={PRIORITY_ICON_FIELD}
								label={formatMessage(messages.iconFieldLabel)}
								defaultValue={initialPriority?.avatarId}
								isRequired
								validate={onValidateIconField}
							>
								{({ error, fieldProps: { id } }) => (
									<Box>
										<Box xcss={IconHelperTextstyles}>
											<Text
												as="p"
												size="small"
												color="color.text.subtle"
												id={messages.iconButtonUgcWarningText.id}
											>
												{formatMessage(messages.iconButtonUgcWarningText)}
											</Text>
										</Box>
										<Inline alignBlock="center" space="space.100">
											<Avatar
												testId="admin-pages-priorities.ui.priority-sidebars.form.form-fields.priority-avatar-icon"
												appearance="square"
												src={selectedAvatarDataUri || initialPriority?.iconUrl}
											/>
											<Button
												onClick={onOpenAvatarPicker}
												type="button"
												aria-describedby={messages.iconButtonUgcWarningText.id}
												id={id}
											>
												{formatMessage(messages.iconButtonLabel)}
											</Button>
											{shouldDisplayAvatarPicker && (
												<ModalAvatarPicker
													setSelectedAvatar={setSelectedAvatar}
													onClose={onCloseAvatarPicker}
												/>
											)}
										</Inline>
										{error && <ErrorMessage>{error}</ErrorMessage>}
									</Box>
								)}
							</Field>
							<Field
								name={PRIORITY_STATUS_COLOR_FIELD}
								label={formatMessage(messages.colorPickerFieldLabel)}
								defaultValue={
									initialPriority && initialPriority.statusColor !== ''
										? initialPriority.statusColor
										: undefined
								}
								isRequired
								validate={onValidateStatusColor}
							>
								{({ fieldProps, error }) => {
									const isColorValid: boolean =
										fieldProps.value === undefined ||
										palette.find((e) => e.value === fieldProps.value) !== undefined;

									const updatedPalette = [
										...palette,
										...(!isColorValid
											? [{ label: fieldProps.value, value: fieldProps.value }]
											: []),
									];

									return (
										<Box xcss={StatusColorFieldStyles}>
											<Stack space="space.100">
												<ColorPicker
													cols={7}
													onChange={(color) => {
														fieldProps.onChange(color);
													}}
													palette={updatedPalette}
													selectedColor={fieldProps.value}
													showDefaultSwatchColor={!!fieldProps.value}
													triggerId={fieldProps.id}
												/>
											</Stack>
											{error && <ErrorMessage>{error}</ErrorMessage>}
											{!isColorValid && (
												<ErrorMessage>
													{formatMessage(messages.invalidColorMessage, { color: fieldProps.value })}
												</ErrorMessage>
											)}
										</Box>
									);
								}}
							</Field>
						</FormSection>
					</form>
				);
			}}
		</Form>
	);
};

const StatusColorFieldStyles = xcss({
	marginBottom: 'space.150',
});
const IconHelperTextstyles = xcss({ marginBottom: 'space.100' });
